import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import { Teacher, isTeacher } from '../../../utils/firebase';
import Button from '../../../components/ui/Button';
import Pagination from '../../../components/ui/Pagination';
import ToggleSwitch from '../../../components/ui/ToggleSwitch';
import TrashIcon from '../../../components/icons/TrashIcon';
import EditIcon from '../../../components/icons/EditIcon';
import SearchIcon from '../../../components/icons/SearchIcon';
import AddNewAchievementDialog from '../../../components/ui/AddNewAchievementDialog';
import AwardAchievementToStudentsDialog from '../../../components/ui/AwardAchievementToStudentsDialog';

interface Achievement {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  type: 'point' | 'firstCome' | 'ranking' | 'manual'; // 포인트 달성, 선착순, 순위, 교사 직접 부여
  status: 'inProgress' | 'completed'; // 진행 중, 완료
  isActive: boolean;
  /** 학생 화면에서 달성 전 비공개 */
  isSecret?: boolean;
  // 달성 조건
  targetValue?: number; // 목표 값 (포인트, 인원 등)
  // 달성 현황 (실제 데이터에서 계산)
  achievedCount?: number; // 달성한 학생 수
  totalStudents?: number; // 전체 학생 수
  createdAt: Date;
  updatedAt: Date;
}

type FilterType = 'all' | 'inProgress' | 'completed';

const AchievementManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<Achievement | null>(null);
  const [totalStudents, setTotalStudents] = useState(0);
  const [awardCounts, setAwardCounts] = useState<Record<string, number>>({});
  const [awardTarget, setAwardTarget] = useState<Achievement | null>(null);

  // 학생 수 조회
  useEffect(() => {
    const fetchStudentCount = async () => {
      if (!currentUser || !isTeacher(currentUser)) return;
      const teacher = currentUser as Teacher;
      
      if (teacher.classId) {
        try {
          const studentsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students`);
          const snapshot = await getDocs(studentsRef);
          setTotalStudents(snapshot.size);
        } catch (err) {
          console.error('학생 수 조회 오류:', err);
        }
      }
    };
    
    fetchStudentCount();
  }, [currentUser]);

  // 업적 목록 조회
  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser)) return;
    
    const teacher = currentUser as Teacher;
    if (!teacher.classId) return;

      setLoading(true);
    const achievementsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/achievements`);
    
    const unsubscribe = onSnapshot(
      achievementsRef,
      async (snapshot) => {
        try {
          const data = await Promise.all(snapshot.docs.map(async (docSnapshot) => {
            const docData = docSnapshot.data();
            // 달성 현황 계산 (실제로는 학생별 업적 달성 데이터를 조회해야 함)
            const achievedCount = 0; // TODO: 실제 달성 학생 수 조회
            const achievement: Achievement = {
              id: docSnapshot.id,
              title: docData.title || '',
              description: docData.description || '',
              imageUrl: docData.imageUrl,
              type: (docData.type as Achievement['type']) || 'point',
              status: docData.status || 'inProgress',
              isActive: docData.isActive ?? true,
              isSecret: docData.isSecret === true,
              targetValue: docData.targetValue,
              achievedCount,
              totalStudents,
              createdAt: docData.createdAt?.toDate() || new Date(),
              updatedAt: docData.updatedAt?.toDate() || new Date()
            };
            return achievement;
          }));
          
        setAchievements(data);
      } catch (err) {
          console.error('업적 조회 오류:', err);
          setError('업적을 불러오는 중 오류가 발생했습니다.');
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('업적 실시간 구독 오류:', err);
        setError('업적을 불러오는 중 오류가 발생했습니다.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, totalStudents]);

  const achievementIdsKey = useMemo(
    () => achievements.map((a) => a.id).sort().join(','),
    [achievements]
  );

  // 교사 직접 부여 업적 — 학생별 부여 건수(실시간)
  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser)) return;
    const teacher = currentUser as Teacher;
    const classId = teacher.classId;
    if (!classId || !achievementIdsKey) return;

    const ids = achievementIdsKey.split(',').filter(Boolean);
    const unsubs = ids.map((id) =>
      onSnapshot(
        collection(
          db,
          'schools',
          teacher.schoolId,
          'classes',
          classId,
          'achievements',
          id,
          'awards'
        ),
        (snap) => {
          setAwardCounts((prev) => ({ ...prev, [id]: snap.size }));
        }
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [currentUser, achievementIdsKey]);

  // 필터링 및 검색
  const filteredAchievements = achievements.filter(achievement => {
    // 필터링
    if (filter === 'inProgress' && achievement.status !== 'inProgress') return false;
    if (filter === 'completed' && achievement.status !== 'completed') return false;
    
    // 검색
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return achievement.title.toLowerCase().includes(query) ||
             achievement.description.toLowerCase().includes(query);
    }
    
    return true;
  });

  // 페이지네이션
  const totalPages = Math.ceil(filteredAchievements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAchievements = filteredAchievements.slice(startIndex, startIndex + itemsPerPage);

  const getDisplayAchievedCount = (achievement: Achievement): number => {
    if (achievement.type === 'manual') {
      return awardCounts[achievement.id] ?? 0;
    }
    return achievement.achievedCount ?? 0;
  };

  // 달성률 계산
  const getAchievementRate = (achievement: Achievement): number => {
    if (!achievement.totalStudents || achievement.totalStudents === 0) return 0;
    const achieved = getDisplayAchievedCount(achievement);
    return Math.round((achieved / achievement.totalStudents) * 100);
  };

  // 업적 타입 텍스트
  const getTypeText = (type: Achievement['type']): string => {
    switch (type) {
      case 'point': return '포인트 달성';
      case 'firstCome': return '선착순';
      case 'ranking': return '순위';
      case 'manual': return '교사 부여';
      default: return '';
    }
  };

  // 상태 텍스트
  const getStatusText = (status: Achievement['status']): string => {
    return status === 'inProgress' ? '진행중' : '완료';
  };

  // 다이얼로그 열기
  const handleOpenDialog = (achievement?: Achievement) => {
    if (achievement) {
      setEditingAchievement(achievement);
    } else {
      setEditingAchievement(null);
    }
    setOpenDialog(true);
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAchievement(null);
  };

  // 업적 저장
  const handleSaveAchievement = async (achievementData: Omit<Achievement, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!currentUser || !isTeacher(currentUser)) return;
    
    const teacher = currentUser as Teacher;
    if (!teacher.classId) return;

    try {
      if (editingAchievement?.id) {
        // 수정
        const achievementRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/achievements`, editingAchievement.id);
        await updateDoc(achievementRef, {
          ...achievementData,
          updatedAt: serverTimestamp()
        });
        setSuccess('업적이 수정되었습니다.');
      } else {
        // 생성
        const achievementsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/achievements`);
        await addDoc(achievementsRef, {
          ...achievementData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        setSuccess('업적이 생성되었습니다.');
      }
      
      handleCloseDialog();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('업적 저장 오류:', err);
      setError('업적 저장 중 오류가 발생했습니다.');
    }
  };

  // 업적 삭제
  const handleDelete = async (id: string) => {
    if (!currentUser || !isTeacher(currentUser)) return;
    
    const teacher = currentUser as Teacher;
    if (!teacher.classId) return;

    if (!window.confirm('정말로 이 업적을 삭제하시겠습니까?')) return;

    try {
      await deleteDoc(doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/achievements`, id));
      setSuccess('업적이 삭제되었습니다.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('업적 삭제 오류:', err);
      setError('업적 삭제 중 오류가 발생했습니다.');
    }
  };

  // 토글 활성화
  const handleToggleActive = async (achievement: Achievement) => {
    if (!currentUser || !isTeacher(currentUser)) return;
    
    const teacher = currentUser as Teacher;
    if (!teacher.classId) return;

    try {
      const achievementRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/achievements`, achievement.id);
      await updateDoc(achievementRef, {
        isActive: !achievement.isActive,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('업적 활성화 토글 오류:', err);
      setError('업적 상태 변경 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>로딩 중...</div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: 'var(--Color-background-main, #F4F6FA)',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      {/* 헤더 */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
        }}
      >
        <div style={{ color: 'var(--Color-text-normal, #161616)', fontSize: '32px', fontFamily: 'Pretendard', fontWeight: '600', lineHeight: '48px' }}>
          업적
        </div>

        {/* 필터 및 검색 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ color: 'var(--Color-text-normal, #161616)', fontSize: '18px', fontFamily: 'Pretendard', fontWeight: '600', lineHeight: '27px' }}>
                  현재
                </span>
                <span style={{ color: 'var(--Color-fill-brand-color, #FF6F0F)', fontSize: '18px', fontFamily: 'Pretendard', fontWeight: '600', lineHeight: '27px' }}>
                  {filteredAchievements.length}
                </span>
                <span style={{ color: 'var(--Color-text-normal, #161616)', fontSize: '18px', fontFamily: 'Pretendard', fontWeight: '600', lineHeight: '27px' }}>
                  개의 업적이 있습니다
                </span>
              </div>
              {/* 필터 버튼 */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <div
                  onClick={() => setFilter('all')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    cursor: 'pointer',
                    padding: '4px 8px'
                  }}
                >
                  <div style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: filter === 'all' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-minor, #BABABA)'
                  }} />
                  <span style={{
                    color: filter === 'all' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                    fontSize: '12px',
                    fontFamily: 'Pretendard',
                    fontWeight: '400',
                    lineHeight: '18px'
                  }}>
                    전체
                  </span>
                </div>
                <div
                  onClick={() => setFilter('inProgress')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    cursor: 'pointer',
                    padding: '4px 8px'
                  }}
                >
                  <div style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: filter === 'inProgress' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-minor, #BABABA)'
                  }} />
                  <span style={{
                    color: filter === 'inProgress' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                    fontSize: '12px',
                    fontFamily: 'Pretendard',
                    fontWeight: '400',
                    lineHeight: '18px'
                  }}>
                    진행 중
                  </span>
                </div>
                <div
                  onClick={() => setFilter('completed')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    cursor: 'pointer',
                    padding: '4px 8px'
                  }}
                >
                  <div style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: filter === 'completed' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-minor, #BABABA)'
                  }} />
                  <span style={{
                    color: filter === 'completed' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                    fontSize: '12px',
                    fontFamily: 'Pretendard',
                    fontWeight: '400',
                    lineHeight: '18px'
                  }}>
                    완료
                  </span>
                </div>
              </div>
            </div>
            <Button
              width="118px"
              height="40px"
              variant="outline"
              onClick={() => handleOpenDialog()}
            >
              업적 만들기
          </Button>
          </div>

          {/* 검색 */}
          <div style={{ width: '100%', height: '40px' }}>
            <div style={{
              width: '100%',
              height: '40px',
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingTop: '8px',
              paddingBottom: '8px',
              background: 'var(--Color-fill-white, white)',
              borderRadius: '999px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: '1px solid var(--Color-line-normal, #F0F0F0)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SearchIcon width={20} height={20} />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="업적 검색"
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '400',
                  lineHeight: '19.5px',
                  minWidth: '259px'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 업적 카드 그리드 */}
      <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          width: '100%'
        }}>
          {paginatedAchievements.map((achievement) => {
            const rate = getAchievementRate(achievement);
            
            return (
              <div
                key={achievement.id}
                style={{
                  width: '308px',
                  height: '535px',
                  background: 'var(--Color-fill-white, white)',
                  boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.05)',
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                  padding: '16px 20px'
                }}
              >
                {/* 이미지 및 상태 */}
                <div style={{
                  width: '100%',
                  height: '179px',
                  background: achievement.imageUrl ? `url(${achievement.imageUrl})` : 'var(--Color-fill-disable, #EDEDED)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  padding: '16px',
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{
                        padding: '2px 12px',
                        background: '#FFD2B5',
                        borderRadius: '8px',
                        color: 'var(--Color-text-brand-color, #FF6F0F)',
                        fontSize: '12px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '18px'
                      }}>
                        {getStatusText(achievement.status)}
                      </div>
                      <div style={{
                        padding: '2px 12px',
                        background: 'var(--Color-fill-white, white)',
                        borderRadius: '8px',
                        border: '1px solid var(--Color-line-normal, #F0F0F0)',
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '12px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '18px'
                      }}>
                        {getTypeText(achievement.type)}
                      </div>
                      {achievement.isSecret && (
                      <div style={{
                        padding: '2px 12px',
                        background: 'var(--Color-fill-sub, #E9E9E9)',
                        borderRadius: '8px',
                        color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                        fontSize: '12px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '18px'
                      }}>
                        시크릿
                      </div>
                      )}
                    </div>
                    <ToggleSwitch
                      checked={achievement.isActive}
                      onChange={() => handleToggleActive(achievement)}
                      size="medium"
                    />
                  </div>
                </div>

                {/* 내용 */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: '18px',
                      fontFamily: 'Pretendard',
                      fontWeight: '600',
                      lineHeight: '27px',
                      height: '27px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {achievement.title}
                    </div>
                    <div style={{
                      color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {achievement.description}
                    </div>
                  </div>

                  {/* 달성 현황 */}
                  <div style={{
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid var(--Color-line-normal, #F0F0F0)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '21px'
                      }}>
                        달성 현황
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                        <span style={{
                          color: 'var(--Color-text-brand-color, #FF6F0F)',
                          fontSize: '12px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '18px'
                        }}>
                          자세히보기
                        </span>
                        <div style={{ width: '16px', height: '16px' }}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M6.27 4.93L9.27 8L6.27 11.07" stroke="#FF6F0F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                    </div>

                    {(achievement.type === 'point' || achievement.type === 'manual') && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{
                          color: 'var(--Color-text-brand-color, #FF6F0F)',
                          fontSize: '28px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '42px'
                        }}>
                          {rate}%
                        </div>
                        <div style={{
                          color: 'var(--Color-text-assistive, #989898)',
                          fontSize: '12px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '18px'
                        }}>
                          ({getDisplayAchievedCount(achievement)}명/{achievement.totalStudents || 0}명)
                        </div>
                        <div style={{
                          width: '100%',
                          height: '16px',
                          background: 'var(--Color-fill-sub, #E9E9E9)',
                          borderRadius: '999px',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${rate}%`,
                            height: '100%',
                            background: 'var(--Color-fill-normal, #293044)',
                            borderRadius: '999px',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                    )}

                    {achievement.type === 'firstCome' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                        <div style={{ display: 'flex', gap: '28px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{
                              color: 'var(--Color-text-brand-color, #FF6F0F)',
                              fontSize: '24px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '36px'
                            }}>
                              {rate}%
                            </div>
                            <div style={{
                              color: 'var(--Color-text-assistive, #989898)',
                              fontSize: '12px',
                              fontFamily: 'Pretendard',
                              fontWeight: '400',
                              lineHeight: '18px'
                            }}>
                              ({getDisplayAchievedCount(achievement)}명/{achievement.totalStudents || 0}명)
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {[1, 2, 3, 4].map((i) => (
                              <div
                                key={i}
                                style={{
                                  width: '45px',
                                  height: '46px',
                                  background: 'var(--Color-fill-hover, #F5F5F5)',
                                  borderRadius: '4px'
                                }}
                              />
          ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {achievement.type === 'ranking' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '32px' }}>
                          {[1, 2, 3].map((rank) => (
                            <div key={rank} style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                              <div style={{
                                width: '48px',
                                height: '48px',
                                background: 'var(--Color-fill-hover, #F5F5F5)',
                                borderRadius: '4px'
                              }} />
                              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '12px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '500',
                                  lineHeight: '18px'
                                }}>
                                  {rank}위
                                </span>
                                <span style={{
                                  color: 'var(--Color-text-normal, #161616)',
                                  fontSize: '12px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: '400',
                                  lineHeight: '18px'
                                }}>
                                  홍길동
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {achievement.type === 'manual' && achievement.isActive && (
                  <div style={{ width: '100%' }}>
                    <Button
                      variant="primary"
                      width="100%"
                      height="44px"
                      onClick={() => setAwardTarget(achievement)}
                    >
                      학생에게 부여
                    </Button>
                  </div>
                )}

                {/* 액션 버튼 */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div
                    onClick={() => handleDelete(achievement.id)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <TrashIcon width={16} height={16} color="#989898" />
                  </div>
                  <div
                    onClick={() => handleOpenDialog(achievement)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <EditIcon width={16} height={16} color="#989898" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={setCurrentPage}
            style={{ padding: '20px 0' }}
          />
        )}

      {/* 업적 생성/수정 다이얼로그 */}
      <AddNewAchievementDialog
        isOpen={openDialog}
        onClose={handleCloseDialog}
        onSave={handleSaveAchievement}
        initialData={editingAchievement || undefined}
        mode={editingAchievement ? 'edit' : 'create'}
      />

      {awardTarget && currentUser && isTeacher(currentUser) && (currentUser as Teacher).classId && (
        <AwardAchievementToStudentsDialog
          isOpen
          onClose={() => setAwardTarget(null)}
          achievementId={awardTarget.id}
          achievementTitle={awardTarget.title}
          schoolId={(currentUser as Teacher).schoolId}
          classId={(currentUser as Teacher).classId!}
          teacherId={currentUser.uid}
        />
      )}

      {/* 성공/에러 메시지 */}
      {success && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 24px',
          background: '#4caf50',
          color: 'white',
          borderRadius: '8px',
          zIndex: 10000,
          fontSize: '14px',
          fontFamily: 'Pretendard'
        }}>
          {success}
        </div>
      )}
      {error && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 24px',
          background: '#f44336',
          color: 'white',
          borderRadius: '8px',
          zIndex: 10000,
          fontSize: '14px',
          fontFamily: 'Pretendard'
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default AchievementManagement; 
